import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading } from '@cotera/client/app/components/ui';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { TemplatedExpressionEditor } from '@cotera/client/app/components/eraql-editor';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cotera/client/v0/components/ui/tabs';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@cotera/client/v0/components/ui/toggle-group';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { DG } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import { err, ok } from 'neverthrow';
import { useMemo, useState } from 'react';
import { z } from 'zod/v3';
import { DatasetModel } from '../model/dataset.model';
import { DATASET_ACTION_GROUP } from './constants';

type Params = {
  expr: string;
};

const PK_SEPARATOR = ' ### ';
const INTERNAL_COLUMNS = ['__cotera_stable_id', '__pk'];

const isIdColumn = (col: string): boolean => {
  const lower = col.toLowerCase();
  // Match "id", "_id", "id_", "_id_", "identifier" but not words containing "id"
  return (
    lower === 'id' ||
    lower.startsWith('id_') ||
    lower.endsWith('_id') ||
    lower.includes('_id_') ||
    lower.includes('identifier')
  );
};

export const extractColumnReferences = (
  expr: string,
  parser: DG.Graph.RelativeParser
): string[] | null => {
  if (expr === '') {
    return [];
  }

  const parseResult = parser.parseExpr(expr);
  if (parseResult.isErr()) {
    return null;
  }

  const ast = parseResult.value.ast;

  // Handle format function calls (template syntax like {{"col"}})
  if (ast.t === 'function-call' && ast.op === 'format') {
    const attrs = ast.args.filter((arg) => arg.t === 'attr');
    const separators = ast.args.filter(
      (arg) => arg.t === 'scalar' && arg.val === PK_SEPARATOR
    );

    // If we have our separator, validate the pattern
    if (separators.length > 0) {
      // Expected: n attrs with (n-1) separators between them
      const expectedSeparators = attrs.length - 1;

      if (separators.length !== expectedSeparators) {
        return null;
      }

      // Check that all args are either attrs, our separator, or empty strings (template literals)
      // Empty strings are OK as they're part of template literal formatting
      for (const arg of ast.args) {
        const isAttr = arg.t === 'attr';
        const isSeparator = arg.t === 'scalar' && arg.val === PK_SEPARATOR;
        const isEmptyString = arg.t === 'scalar' && arg.val === '';

        if (!isAttr && !isSeparator && !isEmptyString) {
          // Found something unexpected (e.g., another string, a function call, etc.)
          return null;
        }
      }

      // Find positions of attrs and separators to validate they alternate
      const attrPositions: number[] = [];
      const separatorPositions: number[] = [];

      ast.args.forEach((arg, i) => {
        if (arg.t === 'attr') {
          attrPositions.push(i);
        } else if (arg.t === 'scalar' && arg.val === PK_SEPARATOR) {
          separatorPositions.push(i);
        }
      });

      // Verify separators are between attrs
      for (let i = 0; i < separatorPositions.length; i++) {
        const sepPos = separatorPositions[i]!;
        const prevAttrPos = attrPositions[i]!;
        const nextAttrPos = attrPositions[i + 1]!;

        // Separator should come after one attr and before the next
        if (sepPos <= prevAttrPos || sepPos >= nextAttrPos) {
          return null;
        }
      }

      return attrs.map((attr) => attr.name);
    }

    // Otherwise, if we have exactly one attr, it's a single column
    if (attrs.length === 1) {
      // Check that all other args are empty strings (template formatting)
      for (const arg of ast.args) {
        const isAttr = arg.t === 'attr';
        const isEmptyString = arg.t === 'scalar' && arg.val === '';

        if (!isAttr && !isEmptyString) {
          // Found something unexpected
          return null;
        }
      }

      return [attrs[0]!.name];
    }
  }

  // Simple attr reference (backward compatibility)
  if (ast.t === 'attr') {
    return [ast.name];
  }

  // Complex expression that doesn't fit our pattern
  return null;
};

export const getAvailableSourceColumns = (
  parser: DG.Graph.RelativeParser,
  datasetConfig: DG.Graph.RelNode | null
): string[] => {
  if (datasetConfig === null) {
    return [];
  }

  // Get columns from source relation, or fall back to all available attributes
  const sourceRelResult = parser.parseRel(datasetConfig.source);
  const columns = sourceRelResult.isOk()
    ? Object.keys(sourceRelResult.value.attributes)
    : parser.availableAttributes();

  // Filter out internal Cotera columns and sort: ID-related columns first, then alphabetically
  return columns
    .filter((col) => !INTERNAL_COLUMNS.includes(col))
    .sort((a, b) => {
      const aIsId = isIdColumn(a);
      const bIsId = isIdColumn(b);
      return aIsId === bIsId ? a.localeCompare(b) : aIsId ? -1 : 1;
    });
};

export const buildPrimaryKeyExpression = (columns: string[]): string => {
  if (columns.length === 0) {
    return '';
  }

  const parts = columns.map((col) => `{{"${col}"}}`);
  return `\`${parts.join(PK_SEPARATOR)}\``;
};

const PK_SUBTITLE = (
  <>
    The primary key uniquely identifies each row.
    <br />
    When a new primary key value appears, all dataset columns are computed for
    that row.
  </>
);

export class SetPrimaryKeyAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Set Primary Key';
  inputSchema = z.object({
    expr: z.string(),
  });
  group = DATASET_ACTION_GROUP;
  icon = 'key';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const dataset = context.targetedModelOfType(DatasetModel);
    if (dataset === null) {
      return false;
    }

    const hasNameInGraph = dataset.currentNameInGraph.snapshot() !== null;
    if (hasNameInGraph === false) {
      return false;
    }

    const config = dataset.config.snapshot();
    const hasPk = (config?.pk ?? '') !== '';

    return hasPk === false;
  }

  async execute(
    { expr }: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const dataset = context.targetedModelOfType(DatasetModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(dataset !== null, 'Dataset not found');
    Assert.assert(graph !== null, 'Graph not found');

    const datasetName = dataset.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null); // by applicable

    const config = dataset.config.snapshot();

    const op = DG.Operations.updateRel({
      ...config,
      symbol: datasetName,
      pk: expr,
    });

    const opResult = await graph.attemptToStageOperations([op]);

    if (opResult.isErr()) {
      toast.error(JSON.stringify(opResult.error.err));
      return err([
        {
          t: opResult.error.err.t,
          message: JSON.stringify(opResult.error.err),
        },
      ]);
    }

    toast.success('Primary key set');

    return ok({});
  }

  shouldAsk(): boolean {
    return true;
  }

  ask(_: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    const targetedDataset = context.targetedModelOfType(DatasetModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(targetedDataset !== null);
    Assert.assert(graph !== null);

    const datasetName = targetedDataset.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null); // by applicable

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                expr={targetedDataset.config.snapshot()?.pk ?? ''}
                parser={graph.graph.snapshot().parser.forRel(datasetName)}
                datasetConfig={targetedDataset.config.snapshot()}
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.pop();
                }}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  parser: DG.Graph.RelativeParser;
  datasetConfig: DG.Graph.RelNode | null;
  expr: string;
}> = ({ onRespond, parser, datasetConfig, expr: initialExpr }) => {
  const [expr, setExpr] = useState(initialExpr);
  const [loading, setLoading] = useState(false);

  const availableColumns = useMemo(
    () => getAvailableSourceColumns(parser, datasetConfig),
    [parser, datasetConfig]
  );

  const { initialColumns, initialMode } = useMemo(() => {
    const columns = extractColumnReferences(initialExpr, parser);

    if (columns !== null) {
      return { initialColumns: columns, initialMode: 'columns' as const };
    }

    return { initialColumns: [], initialMode: 'eraql' as const };
  }, [initialExpr, parser]);

  const [inputMode, setInputMode] = useState<'columns' | 'eraql'>(initialMode);

  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(initialColumns);

  // When columns change, update the expr
  const handleColumnsChange = (cols: string[]) => {
    setSelectedColumns(cols);
    setExpr(buildPrimaryKeyExpression(cols));
  };

  const disabled = parser.parseExpr(expr).isErr();

  const handleSubmit = async () => {
    setLoading(true);
    await onRespond({ expr });
    setLoading(false);
  };

  return (
    <ModalPageView
      title={`Set Primary Key`}
      subtitle={PK_SUBTITLE}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4 h-[280px]"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Tabs
          value={inputMode}
          onValueChange={(v) => setInputMode(v as 'columns' | 'eraql')}
          className="flex-1 min-h-0 flex flex-col"
        >
          <TabsList>
            <TabsTrigger value="columns">Columns</TabsTrigger>
            <TabsTrigger value="eraql">EraQL</TabsTrigger>
          </TabsList>

          <TabsContent value="columns" className="flex-1 min-h-0">
            <div className="flex flex-col gap-2 h-full">
              <label className="text-sm font-medium">
                Select column(s) to use as primary key.
              </label>
              <div className="flex-1 overflow-y-auto p-2">
                <ToggleGroup
                  type="multiple"
                  value={selectedColumns}
                  onValueChange={handleColumnsChange}
                  variant="outline"
                  size="sm"
                  spacing={2}
                >
                  {availableColumns.map((col) => (
                    <ToggleGroupItem
                      key={col}
                      value={col}
                      aria-label={`Toggle ${col}`}
                    >
                      {col}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="eraql" className="flex-1 min-h-0">
            <div className="flex flex-col gap-2 h-full">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">
                  Write an EraQL expression to use as primary key.
                </label>
                <a
                  href="https://cotera.co/docs/reference/eraql/language-reference"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 text-sm"
                  title="View EraQL documentation"
                >
                  <span>Docs</span>
                  <Icon icon="external-link" size="small" />
                </a>
              </div>
              <TemplatedExpressionEditor
                eraql={expr}
                type="expr"
                templated={false}
                parser={parser}
                onChange={(v) => {
                  setExpr(v);
                }}
                inline={true}
                height={160}
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="w-full flex justify-end shrink-0">
          <Button
            disabled={disabled || loading}
            type="submit"
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={handleSubmit}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Set Primary Key'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
